export enum AppointmentStatus {
  Pending = 0,
  Confirmed = 1,
  Rejected = 2,
  CancelledByAdmin = 3,
}

export enum JobType {
  TireHotel = 'TireHotel',
  WheelChange = 'WheelChange',
  OilChange = 'OilChange',
  Service = 'Service',
  Other = 'Other',
  NewTires = 'NewTires',
}

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  [JobType.TireHotel]: 'Hotell',
  [JobType.WheelChange]: 'Hjulskifte',
  [JobType.OilChange]: 'Oljeskift',
  [JobType.Service]: 'Service',
  [JobType.Other]: 'Annet',
  [JobType.NewTires]: 'Nye dekk',
};

export const JOB_TYPE_OPTIONS: JobType[] = [
  JobType.TireHotel,
  JobType.WheelChange,
  JobType.OilChange,
  JobType.Service,
  JobType.Other,
  JobType.NewTires,
];

// Null/unknown job types (older bookings) return an empty string so callers can omit them.
export const jobTypeLabel = (jobType?: string | null): string =>
  (jobType && JOB_TYPE_LABELS[jobType as JobType]) || '';

// Job types where "Hva skal gjøres" (the note) must be filled in. Mirrors the backend validation.
export const jobTypeRequiresNote = (jobType?: string | null): boolean =>
  jobType === JobType.Other || jobType === JobType.NewTires;

export const noteRequiredMessage = (jobType?: string | null): string =>
  `Merknad er påkrevd når jobbtype er ${jobTypeLabel(jobType)}.`;

export interface AppointmentAvailabilityDayDto {
  date: string;
  availableSlotCount: number;
}

export interface AppointmentAvailabilitySlotDto {
  time: string;
}

export enum CustomerBookingOption {
  TireHotelChange = 'TireHotelChange',
  OwnWheelsChange = 'OwnWheelsChange',
}

export interface CustomerBookingOptionDto {
  bookingOption: CustomerBookingOption;
  isAvailable: boolean;
  durationMinutes: number;
  slotIntervalMinutes: number;
}

export interface CustomerBookingEligibilityDto {
  customerExists: boolean;
  hasTireHotel: boolean;
  defaultBookingOption: CustomerBookingOption;
  options: CustomerBookingOptionDto[];
}

export interface CustomerVehicleValidationDto {
  registrationNumber: string;
  brand: string;
  model: string;
}

export interface CustomerCreateAppointmentBookingRequest {
  date: string;
  startTime: string;
  bookingOption: CustomerBookingOption;
  note?: string;
}

export interface RedeemCustomerBookingAccessRequest {
  accessToken: string;
}

export interface CustomerBookingAccessDto {
  token: string;
  expiresAtUtc: string;
  source: string;
}

export interface RedeemCustomerBookingShortLinkDto extends CustomerBookingAccessDto {
  phone: string;
  registrationNumber: string;
}

export interface NextAppointmentDto {
  id: number;
  date: string;
  time: string;
  durationMinutes: number;
  status: AppointmentStatus;
  customerNote?: string;
  adminReason?: string;
  jobType?: string | null;
}

export interface AppointmentAvailabilityRuleDto {
  id: number;
  ruleGroupKey: string;
  name: string;
  startDate: string;
  endDate?: string;
  weekday: number;
  startTime: string;
  endTime: string;
  allow15: boolean;
  allow30: boolean;
  allow60: boolean;
  isActive: boolean;
}

export interface UpsertAppointmentAvailabilityRuleRequest {
  name: string;
  startDate: string;
  endDate?: string;
  weekday?: number;
  weekdays?: number[];
  startTime: string;
  endTime: string;
  allow15: boolean;
  allow30: boolean;
  allow60: boolean;
  isActive: boolean;
}

export interface AppointmentBookingDto {
  id: number;
  customerId: number;
  vehicleId: number;
  customerName: string;
  customerPhone: string;
  registrationNumber: string;
  // True when the booking was made for an unknown customer (no phone or registration number)
  isUnknownCustomer?: boolean;
  date: string;
  time: string;
  durationMinutes: number;
  status: AppointmentStatus;
  customerNote?: string;
  adminReason?: string;
  jobType?: string | null;
  createdBy: string;
  smsFailed: boolean;
  smsErrorDescription?: string;
  // Optional flag populated by backend when vehicle is stored in the tire hotel
  hasTireHotel?: boolean;
  vehicleLocation?: string;
}

export interface AppointmentEvaluationLinkDto {
  id: number | null;
  season?: string;
  performedBy?: string;
  performedDate?: string;
  registrationNumber?: string;
  rating?: string;
}

export interface AdminCreateAppointmentBookingRequest {
  customerName: string;
  phone: string;
  registrationNumber: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  note?: string;
  jobType?: JobType | '';
  unknownCustomer: boolean;
  createdBy?: string;
}

export interface AdminManualBookingCustomerLookupDto {
  registrationNumber: string;
  customerName?: string;
  phone?: string;
}

export interface AdminManualBookingSvvLookupDto {
  brand: string;
  model: string;
}

export interface CancelAppointmentBookingRequest {
  reason?: string;
  cancelledBy?: string;
}
